// Theme treatment system: tokens stored as JSON in ui_themes.tokens,
// applied at runtime by injecting CSS variables on :root.

export type ThemeTokens = {
  // Colors are OKLCH or any valid CSS color string (hex works too).
  background?: string;
  foreground?: string;
  card?: string;
  cardForeground?: string;
  primary?: string;
  primaryForeground?: string;
  secondary?: string;
  accent?: string;
  border?: string;
  // Typography
  fontDisplay?: string; // e.g. '"Bricolage Grotesque", system-ui, sans-serif'
  fontBody?: string;
  fontStylesheetUrl?: string; // remote stylesheet (Google Fonts CSS2 URL)
  // Shape & depth
  radiusRem?: number; // base radius in rem (default 0.75)
  shadowStyle?: "soft" | "medium" | "dramatic" | "flat";
  // Mode
  defaultMode?: "dark" | "light";
};

export const FONT_PRESETS: Record<string, { label: string; display: string; body: string; url: string }> = {
  default: {
    label: "Bricolage + Inter (default)",
    display: '"Bricolage Grotesque", "Inter", system-ui, sans-serif',
    body: '"Inter", system-ui, -apple-system, sans-serif',
    url: "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap",
  },
  editorial: {
    label: "Fraunces + Inter",
    display: '"Fraunces", Georgia, serif',
    body: '"Inter", system-ui, sans-serif',
    url: "https://fonts.googleapis.com/css2?family=Fraunces:wght@500;700;900&family=Inter:wght@400;500;600;700&display=swap",
  },
  techno: {
    label: "Space Grotesk + JetBrains Mono",
    display: '"Space Grotesk", system-ui, sans-serif',
    body: '"JetBrains Mono", ui-monospace, monospace',
    url: "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&family=Space+Grotesk:wght@500;600;700&display=swap",
  },
  retro: {
    label: "Bowlby + Outfit",
    display: '"Bowlby One", "Bricolage Grotesque", sans-serif',
    body: '"Outfit", system-ui, sans-serif',
    url: "https://fonts.googleapis.com/css2?family=Bowlby+One&family=Outfit:wght@400;500;600;700&display=swap",
  },
  minimal: {
    label: "Manrope only",
    display: '"Manrope", system-ui, sans-serif',
    body: '"Manrope", system-ui, sans-serif',
    url: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap",
  },
};

export const SHADOW_PRESETS: Record<NonNullable<ThemeTokens["shadowStyle"]>, { glow: string; strong: string }> = {
  flat: { glow: "none", strong: "none" },
  soft: {
    glow: "0 4px 16px -6px oklch(from var(--primary) l c h / 25%)",
    strong: "0 8px 24px -6px oklch(from var(--primary) l c h / 35%)",
  },
  medium: {
    glow: "0 0 40px -8px oklch(from var(--primary) l c h / 55%)",
    strong: "0 0 60px -4px oklch(from var(--primary) l c h / 70%)",
  },
  dramatic: {
    glow: "0 0 60px -4px oklch(from var(--primary) l c h / 70%), 0 0 120px -20px oklch(from var(--primary) l c h / 40%)",
    strong: "0 0 90px -2px oklch(from var(--primary) l c h / 85%), 0 0 180px -30px oklch(from var(--primary) l c h / 55%)",
  },
};

const STYLE_ID = "glg-theme-overrides";
const FONT_LINK_ID = "glg-theme-fonts";

export function applyTheme(tokens: ThemeTokens) {
  if (typeof document === "undefined") return;

  // Inject / replace font stylesheet link
  const url = tokens.fontStylesheetUrl?.trim();
  let link = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
  if (url) {
    if (!link) {
      link = document.createElement("link");
      link.id = FONT_LINK_ID;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (link.href !== url) link.href = url;
  } else if (link) {
    link.remove();
  }

  // Build CSS variable overrides
  const map: Record<string, string | undefined> = {
    "--background": tokens.background,
    "--foreground": tokens.foreground,
    "--card": tokens.card,
    "--card-foreground": tokens.cardForeground,
    "--primary": tokens.primary,
    "--primary-foreground": tokens.primaryForeground,
    "--secondary": tokens.secondary,
    "--accent": tokens.accent,
    "--border": tokens.border,
    "--font-display": tokens.fontDisplay,
    "--font-sans": tokens.fontBody,
    "--radius": tokens.radiusRem ? `${tokens.radiusRem}rem` : undefined,
  };

  if (tokens.shadowStyle) {
    const s = SHADOW_PRESETS[tokens.shadowStyle];
    map["--shadow-glow"] = s.glow;
    map["--shadow-glow-strong"] = s.strong;
  }

  const lines = Object.entries(map)
    .filter(([, v]) => v != null && String(v).trim() !== "")
    .map(([k, v]) => `  ${k}: ${v};`)
    .join("\n");

  const css = `:root {\n${lines}\n}`;

  let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = STYLE_ID;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = css;

  // Default mode
  if (tokens.defaultMode === "light") {
    document.documentElement.classList.remove("dark");
  } else {
    document.documentElement.classList.add("dark");
  }
}

export function applyFavicon(url: string | null | undefined) {
  if (typeof document === "undefined" || !url) return;
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"][data-glg]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    link.setAttribute("data-glg", "1");
    document.head.appendChild(link);
  }
  link.href = url;
}
